import React from 'react';
import { Box, Typography, Paper, Chip } from '@mui/material';
import {
  CheckCircle as CheckIcon,
  NotificationsActive as SignalIcon,
} from '@mui/icons-material';

const CompletionStep = ({ selectedAssets, selectedSignals }) => {
  return (
    <Box>
      <Box
        sx={{
          textAlign: 'center',
          mb: 4,
        }}
      >
        <CheckIcon
          sx={{
            fontSize: 80,
            color: 'var(--dwlf-up)',
            mb: 2,
          }}
        />
        <Typography
          variant="h4"
          sx={{
            fontFamily: '"Space Grotesk", sans-serif',
            fontWeight: 600,
            color: 'var(--dwlf-up)',
            mb: 1,
          }}
        >
          You're all set!
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: 'var(--dwlf-ink-70)',
            fontFamily: '"IBM Plex Mono", monospace',
          }}
        >
          Your trading workspace is configured and ready to go
        </Typography>
      </Box>

      {/* Summary */}
      <Paper
        sx={{
          p: 3,
          bgcolor: 'var(--dwlf-elevated)',
          border: '1px solid var(--dwlf-ink-08)',
          borderRadius: 0,
          mb: 3,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontFamily: '"Space Grotesk", sans-serif',
            fontWeight: 600,
            mb: 2,
          }}
        >
          Your Watchlist
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
          {selectedAssets.slice(0, 8).map((asset) => (
            <Chip
              key={asset.symbol}
              label={asset.symbol}
              size="small"
              sx={{
                bgcolor: 'var(--dwlf-accent-10)',
                color: 'var(--dwlf-accent)',
                border: '1px solid var(--dwlf-accent-30)',
                borderRadius: 0,
                fontFamily: '"Space Mono", monospace',
                fontSize: '0.75rem',
              }}
            />
          ))}
          {selectedAssets.length > 8 && (
            <Chip
              label={`+${selectedAssets.length - 8} more`}
              size="small"
              sx={{
                bgcolor: 'var(--dwlf-ink-05)',
                color: 'var(--dwlf-ink-50)',
                border: '1px solid var(--dwlf-ink-10)',
                borderRadius: 0,
                fontFamily: '"Space Mono", monospace',
                fontSize: '0.75rem',
              }}
            />
          )}
        </Box>

        <Typography
          variant="h6"
          sx={{
            fontFamily: '"Space Grotesk", sans-serif',
            fontWeight: 600,
            mb: 2,
          }}
        >
          Signal Preferences
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {selectedSignals.map((signalId) => {
            const labels = {
              momentum: 'Momentum Reversals',
              trend: 'Trend Following',
              sr: 'S/R Touches',
            };
            return (
              <Chip
                key={signalId}
                label={labels[signalId] || signalId}
                size="small"
                icon={<SignalIcon sx={{ fontSize: 16 }} />}
                sx={{
                  bgcolor: 'var(--dwlf-up-10)',
                  color: 'var(--dwlf-up)',
                  border: '1px solid var(--dwlf-up-30)',
                  borderRadius: 0,
                  fontFamily: '"Space Mono", monospace',
                  fontSize: '0.75rem',
                  '& .MuiChip-icon': {
                    color: 'var(--dwlf-up)',
                  },
                }}
              />
            );
          })}
        </Box>
      </Paper>

      {/* Preview */}
      <Paper
        sx={{
          p: 3,
          bgcolor: 'var(--dwlf-accent-05)',
          border: '1px solid var(--dwlf-accent-20)',
          borderRadius: 0,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: 'var(--dwlf-accent)',
            fontFamily: '"Space Mono", monospace',
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            mb: 1,
            fontSize: '0.75rem',
          }}
        >
          What's Next?
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: 'var(--dwlf-text)',
            fontFamily: '"IBM Plex Mono", monospace',
            mb: 1,
          }}
        >
          • View live charts for your watchlist assets
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: 'var(--dwlf-text)',
            fontFamily: '"IBM Plex Mono", monospace',
            mb: 1,
          }}
        >
          • Receive signal notifications as they fire
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: 'var(--dwlf-text)',
            fontFamily: '"IBM Plex Mono", monospace',
          }}
        >
          • Build custom strategies and backtest them
        </Typography>
      </Paper>
    </Box>
  );
};

export default CompletionStep;
